/**
 * Pure helpers for the User Management create/edit form (Contact-User, CTU-1.1):
 * form <-> request conversion, the edit diff, and server field-error routing.
 */
import { CONTACT_STATUS, NON_USER_ID } from '@app-types/user';
import { getApiErrorMessage, getApiErrorStatus, getApiFieldErrors } from '@utils/apiError';
import type { AdminUser, CreateUserRequest } from '@app-types/admin';
import type { DivisionOption } from '@app-types/division';
import type { ContactStatus } from '@app-types/user';
import type {
  AdditionalContactDataKey,
  CanLoginFieldState,
  UserIdFieldState,
  ContactStatusFormValue,
  CreatePayloadOptions,
  EditFormOriginal,
  ErrorTargetField,
  FieldErrorSplit,
  NoteField,
  UpdateableUserField,
  UserFieldChanges,
  UserFormData,
  UserFormSubmitError,
  UserFormValue
} from '@app-types/userForm';

// AdditionalContactData: the form and requests use `additional_contact_data_N`,
// responses use `additional_contact_dataN` (snakecase-keys does not split digits).
export const ADDITIONAL_CONTACT_DATA_KEYS: readonly AdditionalContactDataKey[] = [
  { formKey: 'additional_contact_data_1', responseKey: 'additional_contact_data1' },
  { formKey: 'additional_contact_data_2', responseKey: 'additional_contact_data2' },
  { formKey: 'additional_contact_data_3', responseKey: 'additional_contact_data3' }
];

// Text fields that legacy rows may hold as null: a null original compares equal to ''.
const NULLABLE_TEXT_FIELDS = new Set<UpdateableUserField>([
  'preferred_name',
  'contact_id',
  'department',
  'cost_center',
  ...ADDITIONAL_CONTACT_DATA_KEYS.map((k) => k.formKey)
]);

const UPDATEABLE_FIELDS: readonly UpdateableUserField[] = [
  'first_name',
  'last_name',
  'preferred_name',
  'email',
  'role_ids',
  'can_login',
  'division_id',
  'contact_id',
  'user_id',
  'contact_status',
  'department',
  'cost_center',
  ...ADDITIONAL_CONTACT_DATA_KEYS.map((k) => k.formKey)
];

// Matched by name, as the server does when it applies the default Division (R7).
const UNIDENTIFIED_DIVISION_NAME = 'Unidentified';

const INACTIVE_CANNOT_LOGIN = 'Inactive users cannot login';
const NO_AUTH_METHOD = 'No auth method - select a role first';
const CONTACT_USER_ID_LOCKED = 'Contacts use NON-USER until a role is assigned';
export const CONTACT_STATUS_REQUIRED = 'Contact Status is required';
export const USER_ID_REQUIRED_FOR_ROLES = 'UserID is required when a role is assigned';

export const booleanToSelectValue = (value: boolean | undefined): string => {
  if (value === undefined || value === null) return 'true';
  return value ? 'true' : 'false';
};

export const selectValueToBoolean = (value: string): boolean => value === 'true';

export const isContactStatus = (value: unknown): value is ContactStatus =>
  value === CONTACT_STATUS.ACTIVE || value === CONTACT_STATUS.INACTIVE;

/** Contact Status as the form holds it: a known status, or '' when none is chosen. */
export const toContactStatusFormValue = (value: unknown): ContactStatusFormValue =>
  isContactStatus(value) ? value : '';

/** ContactStatus shown on the edit form: the stored value, or derived from is_active for rows without one. */
export const initialContactStatus = (user: AdminUser | null | undefined): ContactStatus => {
  if (user?.contact_status) return user.contact_status;
  return user?.is_active === false ? CONTACT_STATUS.INACTIVE : CONTACT_STATUS.ACTIVE;
};

const textValue = (value: UserFormValue | undefined): string => (typeof value === 'string' ? value : '');

/** Empty or NON-USER (any case) is the shared default identity. */
export const isDefaultUserId = (value: UserFormValue | undefined): boolean => {
  const text = textValue(value).trim();
  return text === '' || text.toLowerCase() === NON_USER_ID.toLowerCase();
};

/** Empty or NON-USER becomes NON-USER; otherwise the trimmed value. */
export const normalizeUserId = (value: UserFormValue | undefined): string =>
  isDefaultUserId(value) ? NON_USER_ID : textValue(value).trim();

// Optional text left blank is omitted from the create body.
const optionalTextValue = (value: UserFormValue | undefined): string | undefined => {
  const text = textValue(value);
  return text.trim() === '' ? undefined : text;
};

/**
 * The Note to send with the record, trimmed, for spreading into a create or edit body.
 * A blank Note gives no key, so no note is sent (ADO-811).
 */
export const toNoteField = (value: UserFormValue | undefined): NoteField => {
  const note = optionalTextValue(value)?.trim();
  return note === undefined ? {} : { note };
};

/**
 * The Division a Contact-User without one is in (R7): the active "Unidentified" division.
 * @returns Its id, or null when it has been renamed, deleted or deactivated
 */
export const findUnidentifiedDivisionId = (divisions: readonly DivisionOption[]): number | null =>
  divisions.find((div) => div.name.toLowerCase() === UNIDENTIFIED_DIVISION_NAME.toLowerCase())?.id ?? null;

/** The multiselect's role ids (strings) as the numeric ids the API expects. */
export const toRoleIds = (value: UserFormValue | undefined): number[] =>
  Array.isArray(value) ? value.map((id) => Number.parseInt(id, 10)) : [];

/** The role ids the form holds, as strings (the multiselect's option values). */
export const toRoleIdStrings = (value: UserFormValue | undefined): string[] => (Array.isArray(value) ? value : []);

/**
 * Create body (contract 3.1). Never carries is_active. division_id is always sent, whatever
 * the roles: the form preselects Unidentified (R7), and a blank one goes as null, which
 * the server resolves to Unidentified.
 */
export const buildCreatePayload = (formData: UserFormData, options: CreatePayloadOptions): CreateUserRequest => {
  // Contact Status is a required select, so the form never submits without one.
  if (!isContactStatus(formData.contact_status)) throw new Error(CONTACT_STATUS_REQUIRED);
  const roleIds = toRoleIds(formData.role_ids);
  const payload: CreateUserRequest = {
    email: textValue(formData.email),
    first_name: textValue(formData.first_name),
    last_name: textValue(formData.last_name),
    contact_id: textValue(formData.contact_id),
    user_id: normalizeUserId(formData.user_id),
    contact_status: formData.contact_status,
    department: textValue(formData.department),
    cost_center: textValue(formData.cost_center),
    role_ids: roleIds,
    auth_source: options.authSource,
    // Contacts (no role) cannot log in; Login Access is locked off in the form.
    can_login:
      roleIds.length === 0
        ? false
        : formData.can_login !== undefined
          ? selectValueToBoolean(String(formData.can_login))
          : undefined
  };

  const preferredName = options.canSetPreferredName ? optionalTextValue(formData.preferred_name) : undefined;
  if (preferredName !== undefined) payload.preferred_name = preferredName;

  ADDITIONAL_CONTACT_DATA_KEYS.forEach(({ formKey }) => {
    const value = optionalTextValue(formData[formKey]);
    if (value !== undefined) payload[formKey] = value;
  });

  payload.division_id =
    formData.division_id === '' || formData.division_id === undefined ? null : Number(formData.division_id);

  return { ...payload, ...toNoteField(formData.note) };
};

/**
 * Splits server field errors into those a visible, editable field on the form can
 * show and the rest (hidden, read-only or absent fields), which go to a toast.
 */
export const splitFieldErrors = (
  fieldErrors: Record<string, string>,
  fields: readonly ErrorTargetField[]
): FieldErrorSplit => {
  const shown = new Set(fields.filter((f) => !f.hidden && f.type !== 'readonly').map((f) => f.name));
  const onForm: Record<string, string> = {};
  const elsewhere: string[] = [];
  Object.entries(fieldErrors).forEach(([field, message]) => {
    if (shown.has(field)) onForm[field] = message;
    else elsewhere.push(message);
  });
  return { onForm, elsewhere };
};

/**
 * How a failed create/edit is shown. Server field errors go under their fields (the
 * rest to a toast); failing that, a 409 maps to `conflictErrors` when given (the
 * create form's duplicate-email message); anything else becomes a toast.
 */
export const resolveUserFormSubmitError = (
  error: unknown,
  fields: readonly ErrorTargetField[],
  fallbackMessage: string,
  conflictErrors?: Record<string, string>
): UserFormSubmitError => {
  const { onForm, elsewhere } = splitFieldErrors(getApiFieldErrors(error), fields);
  if (Object.keys(onForm).length > 0 || elsewhere.length > 0) {
    return { fieldErrors: onForm, toastMessage: elsewhere.length > 0 ? elsewhere.join(' ') : undefined };
  }
  if (conflictErrors && getApiErrorStatus(error) === 409) {
    return { fieldErrors: conflictErrors };
  }
  return { fieldErrors: {}, toastMessage: getApiErrorMessage(error, fallbackMessage) };
};

export const withoutKey = (errors: Record<string, string>, key: string): Record<string, string> => {
  if (!(key in errors)) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
};

/**
 * Values the form must take after a field change: an Inactive contact cannot log in,
 * so choosing Inactive turns Login Access off. Returns undefined when nothing changes.
 */
export const getContactStatusOverrides = (name: string, value: UserFormValue): Partial<UserFormData> | undefined =>
  name === 'contact_status' && value === CONTACT_STATUS.INACTIVE
    ? { can_login: 'false', user_id: NON_USER_ID, role_ids: [], auth_source: 'None' }
    : undefined;

/** Login Access is locked off without a role (no auth method) or for an Inactive contact. */
export const getCanLoginFieldState = (
  roleIds: readonly string[],
  contactStatus: ContactStatusFormValue
): CanLoginFieldState => {
  if (contactStatus === CONTACT_STATUS.INACTIVE) return { disabled: true, placeholder: INACTIVE_CANNOT_LOGIN };
  if (roleIds.length === 0) return { disabled: true, placeholder: NO_AUTH_METHOD };
  return { disabled: false };
};

/** A contact (no role) cannot hold a real UserID; the field is locked to NON-USER. */
export const getUserIdFieldState = (roleIds: readonly string[]): UserIdFieldState => {
  if (roleIds.length === 0) return { disabled: true, placeholder: CONTACT_USER_ID_LOCKED };
  return { disabled: false, placeholder: 'Enter a unique UserID' };
};

/**
 * Role changes drive UserID so a contact cannot keep a real id, and assigning a
 * role cannot silently reuse NON-USER. Adding a further role leaves the typed id.
 */
export const getRoleChangeUserIdOverrides = (
  name: string,
  value: UserFormValue,
  previousRoleIds: readonly string[]
): Partial<UserFormData> | undefined => {
  if (name !== 'role_ids') return undefined;
  const hasRoles = toRoleIdStrings(value).length > 0;
  if (!hasRoles) return { user_id: NON_USER_ID };
  if (previousRoleIds.length === 0) return { user_id: '' };
  return undefined;
};

const roleIdsChange = (formValue: unknown, originalValue: unknown) => {
  const newValue = Array.isArray(formValue) ? formValue.map((id) => Number.parseInt(String(id), 10)) : [];
  const origValue: unknown[] = Array.isArray(originalValue) ? originalValue : [];
  const hasChanged = newValue.length !== origValue.length || !newValue.every((v) => origValue.includes(v));
  return { hasChanged, newValue };
};

const divisionIdChange = (formValue: unknown, originalValue: unknown) => {
  const newValue = formValue === '' ? null : Number.parseInt(String(formValue), 10);
  const origValue = originalValue ? Number.parseInt(String(originalValue), 10) : null;
  return { hasChanged: newValue !== origValue, newValue };
};

const fieldChange = (
  field: UpdateableUserField,
  formValue: unknown,
  originalValue: unknown
): { hasChanged: boolean; newValue: unknown } => {
  if (field === 'role_ids') return roleIdsChange(formValue, originalValue);
  if (field === 'division_id') return divisionIdChange(formValue, originalValue);
  if (field === 'user_id') {
    const newValue = normalizeUserId(formValue as UserFormValue | undefined);
    return { hasChanged: normalizeUserId(originalValue as UserFormValue | undefined) !== newValue, newValue };
  }

  if (field === 'can_login') {
    const newValue = selectValueToBoolean(String(formValue));
    return { hasChanged: originalValue !== newValue, newValue };
  }

  if (NULLABLE_TEXT_FIELDS.has(field)) {
    const newValue = formValue ?? '';
    return { hasChanged: String(originalValue ?? '') !== String(newValue), newValue };
  }

  return { hasChanged: originalValue !== formValue, newValue: formValue };
};

/**
 * The stored user in edit-form shape, so the diff compares like with like:
 * ContactStatus falls back to is_active, AdditionalContactData uses the request keys, and a user
 * with no Division is in Unidentified (as the form shows), so saving other fields does not set it.
 * @param user - The stored user
 * @param defaultDivisionId - findUnidentifiedDivisionId of the active divisions, or null
 */
export const toEditFormOriginal = (user: AdminUser, defaultDivisionId: number | null): EditFormOriginal => {
  const original: EditFormOriginal = {
    first_name: user.first_name,
    last_name: user.last_name,
    preferred_name: user.preferred_name,
    email: user.email,
    role_ids: user.role_ids,
    can_login: user.can_login,
    division_id: user.division_id ?? defaultDivisionId,
    contact_id: user.contact_id,
    user_id: user.user_id,
    contact_status: initialContactStatus(user),
    department: user.department,
    cost_center: user.cost_center
  };
  ADDITIONAL_CONTACT_DATA_KEYS.forEach(({ formKey, responseKey }) => {
    original[formKey] = user[responseKey];
  });
  return original;
};

/**
 * The edit form's starting values for the selected user (empty when none is selected).
 * @param user - The selected user
 * @param defaultDivisionId - findUnidentifiedDivisionId of the active divisions (null if none), shown for a user with no Division
 */
export const toEditFormInitialData = (user: AdminUser | null, defaultDivisionId: number | null): UserFormData => {
  const initialData: UserFormData = {
    email: user?.email ?? '',
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    preferred_name: user?.preferred_name ?? '',
    auth_source: user?.auth_source ?? '',
    role_ids: user?.role_ids?.map((id) => id.toString()) || [],
    division_id: user?.division_id?.toString() || defaultDivisionId?.toString() || '',
    contact_id: user?.contact_id ?? '',
    user_id: user?.role_ids && user.role_ids.length > 0 ? (user.user_id ?? '') : NON_USER_ID,
    contact_status: initialContactStatus(user),
    department: user?.department ?? '',
    cost_center: user?.cost_center ?? '',
    can_login: booleanToSelectValue(user?.can_login),
    note: ''
  };
  ADDITIONAL_CONTACT_DATA_KEYS.forEach(({ formKey, responseKey }) => {
    initialData[formKey] = user?.[responseKey] ?? '';
  });
  return initialData;
};

/**
 * Only the changed fields go into the PUT body. is_active is not sent from the form
 * (ContactStatus replaces it; Deactivate still sends it). division_id is diffed like any
 * other field, for every user whatever their roles.
 */
export const getChangedFields = (originalData: EditFormOriginal, formData: UserFormData): UserFieldChanges => {
  const changes: UserFieldChanges = {};

  UPDATEABLE_FIELDS.forEach((field) => {
    const formValue = formData[field];
    if (formValue === undefined) return;

    const { hasChanged, newValue } = fieldChange(field, formValue, originalData[field]);
    if (hasChanged) changes[field] = newValue;
  });

  return changes;
};

export const isEligibleForTemporaryPassword = (user: AdminUser): boolean => {
  if (!user.can_login || !user.is_active) return false;
  if (user.auth_source === 'local') return true;
  return user.auth_source === 'keycloak' && !user.graph_user_id;
};
